import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Info, Lock, Share2, Eye, Globe, Mail, ChevronRight, User, PhoneCall } from 'lucide-react';
import { Link } from 'react-router-dom';
import './ZiroPrivacyPolicyPage.css';

const ZiroPrivacyPolicyPage = () => {
    React.useEffect(() => {
        document.title = "Privacy Policy - Ziro";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="ziro-policy-container">
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="ziro-policy-content"
            >
                <header className="ziro-policy-header">
                    <h1>Privacy Policy for Ziro: HD Video Calls and Chats</h1>
                    <p className="ziro-policy-date">Effective Date: May 11, 2026</p>
                </header>

                <div className="ziro-policy-intro">
                    <p>
                        Welcome to <strong>Ziro: HD Video Calls and Chats</strong> (“Ziro”, “we”, “our”, or “us”).
                        Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services.
                    </p>
                    <p style={{ marginTop: '1rem' }}>
                        By using Ziro, you agree to the terms of this Privacy Policy.
                    </p>
                </div>

                <hr style={{ margin: '3rem 0', opacity: 0.1 }} />

                <section className="ziro-policy-section">
                    <h2><Info size={24} color="#6366f1" /> 1. Information We Collect</h2>
                    <p>We may collect the following types of information:</p>
                    
                    <h3>1.1 Personal Information</h3>
                    <ul className="ziro-policy-list">
                        <li>Name</li>
                        <li>Email address</li>
                        <li>Profile information (if provided)</li>
                    </ul>

                    <h3>1.2 Authentication Data</h3>
                    <ul className="ziro-policy-list">
                        <li>Google account information (if you use Google Sign-In)</li>
                        <li>Login credentials (email and password)</li>
                    </ul>

                    <h3>1.3 Communication Data</h3>
                    <ul className="ziro-policy-list">
                        <li>Messages you send and receive within the app</li>
                        <li>Media files (photos, videos, audio) shared through chats</li>
                    </ul>

                    <h3>1.4 Audio & Video Data</h3>
                    <ul className="ziro-policy-list">
                        <li>Voice and video streams during calls (processed in real-time)</li>
                    </ul>

                    <h3>1.5 Device & Technical Data</h3>
                    <ul className="ziro-policy-list">
                        <li>Device identifiers (such as Advertising ID, Android ID)</li>
                        <li>IP address</li>
                        <li>Device type, OS version</li>
                        <li>Network information</li>
                    </ul>

                    <h3>1.6 App Usage Data</h3>
                    <ul className="ziro-policy-list">
                        <li>App interactions</li>
                        <li>Features used</li>
                        <li>Crash logs and performance data</li>
                    </ul>

                    <h3>1.7 Location Information</h3>
                    <ul className="ziro-policy-list">
                        <li>Precise and approximate location data</li>
                        <li><strong>Note:</strong> Location data is only accessed when you explicitly choose to use the live location sharing feature. It is only collected while you are actively sharing your location.</li>
                    </ul>
                </section>

                <section className="ziro-policy-section">
                    <h2><Eye size={24} color="#6366f1" /> 2. How We Use Your Information</h2>
                    <p>We use your data to:</p>
                    <ul className="ziro-policy-list">
                        <li>Provide and maintain our services</li>
                        <li>Enable messaging and media sharing</li>
                        <li>Enable voice and video calls</li>
                        <li>Authenticate users</li>
                        <li>Improve app performance and user experience</li>
                        <li>Monitor and prevent fraud or abuse</li>
                        <li>Enable live location sharing features</li>
                        <li>Comply with legal obligations</li>
                    </ul>
                </section>

                <section className="ziro-policy-section">
                    <h2><Share2 size={24} color="#6366f1" /> 3. Data Sharing and Disclosure</h2>
                    <p>We may share your information with:</p>

                    <h3>3.1 Service Providers</h3>
                    <p>We use third-party services that may process data, including:</p>
                    <ul className="ziro-policy-list">
                        <li>Google (Authentication, Firebase services, Analytics, AdMob)</li>
                        <li>Cloud hosting providers</li>
                    </ul>

                    <h3>3.2 Legal Requirements</h3>
                    <p>We may disclose your data if required by law or to protect our rights.</p>
                </section>

                <section className="ziro-policy-section">
                    <h2><Lock size={24} color="#6366f1" /> 4. Data Security</h2>
                    <p>We implement appropriate security measures:</p>
                    <ul className="ziro-policy-list">
                        <li>All data is encrypted in transit using HTTPS, TLS, and secure protocols</li>
                        <li>WebRTC calls are encrypted using industry-standard encryption (DTLS/SRTP)</li>
                    </ul>
                    <p style={{ marginTop: '1rem', fontStyle: 'italic', fontSize: '0.9rem' }}>However, no system is 100% secure.</p>
                </section>

                <section className="ziro-policy-section">
                    <h2><Shield size={24} color="#6366f1" /> 5. Data Retention</h2>
                    <p>We retain your data:</p>
                    <ul className="ziro-policy-list">
                        <li>As long as your account is active</li>
                        <li>Until you request deletion</li>
                        <li>For a limited time afterward if required by law or for security purposes</li>
                    </ul>
                </section>

                <section className="ziro-policy-section">
                    <h2><User size={24} color="#6366f1" /> 6. Your Rights</h2>
                    <p>You have the right to:</p>
                    <ul className="ziro-policy-list">
                        <li>Access your data</li>
                        <li>Request correction of your data</li>
                        <li>Request deletion of your data</li>
                    </ul>

                    <h3 style={{ marginTop: '2rem' }}>🔗 Account Deletion</h3>
                    <p>You can request deletion of your account and data here:</p>
                    
                    <Link to="/delete-account-ziro" className="ziro-policy-link-card">
                        <div className="ziro-policy-link-text">
                            Request Account & Data Deletion
                        </div>
                        <div className="ziro-policy-link-box">
                            Click Here <ChevronRight size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
                        </div>
                    </Link>
                    
                    <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#94a3b8' }}>
                        We will process deletion requests within a reasonable time.
                    </p>
                </section>

                <section className="ziro-policy-section">
                    <h2><Eye size={24} color="#6366f1" /> 7. Children's Privacy</h2>
                    <p>Ziro is not intended for children under 13 (or applicable age in your region). We do not knowingly collect data from children.</p>
                </section>

                <section className="ziro-policy-section">
                    <h2><Globe size={24} color="#6366f1" /> 8. Third-Party Services</h2>
                    <p>Ziro may use third-party SDKs and services which may collect data in accordance with their own privacy policies.</p>
                    <p>We recommend reviewing their policies for more details.</p>
                </section>

                <section className="ziro-policy-section">
                    <h2><Globe size={24} color="#6366f1" /> 9. International Data Transfers</h2>
                    <p>Your data may be processed in countries outside your own, where data protection laws may differ.</p>
                </section>

                <section className="ziro-policy-section">
                    <h2><Info size={24} color="#6366f1" /> 10. Changes to This Privacy Policy</h2>
                    <p>We may update this Privacy Policy from time to time. We will notify users of significant changes by updating the “Effective Date”.</p>
                </section>

                <section className="ziro-policy-section">
                    <h2><Mail size={24} color="#6366f1" /> 11. Contact Us</h2>
                    <p>If you have any questions or concerns, contact us at:</p>
                    <ul className="ziro-policy-list">
                        <li>📧 Email: <a href="mailto:support@ziroapp.com" style={{ color: '#6366f1', textDecoration: 'none' }}>support@ziroapp.com</a></li>
                        <li>🌐 Website: <a href="https://quivixsite.netlify.app" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>https://quivixsite.netlify.app</a></li>
                    </ul>
                </section>

                <section className="ziro-policy-section">
                    <h2><PhoneCall size={24} color="#6366f1" /> 12. Consent</h2>
                    <p>By using Ziro, you consent to this Privacy Policy.</p>
                </section>

                <div style={{ marginTop: '4rem', textAlign: 'center', opacity: 0.5, fontSize: '0.8rem' }}>
                    © 2026 Quivix Digital. All Rights Reserved.
                </div>
            </motion.div>
        </div>
    );
};

export default ZiroPrivacyPolicyPage;
